import { Field } from "@base-ui/react/field";
import type { InputProps as BaseInputProps } from "@base-ui/react/input";
import { Input as BaseInput } from "@base-ui/react/input";
import type React from "react";
import { type ForwardedRef, forwardRef, useRef, useState } from "react";
import { NumberFormatBase } from "react-number-format";
import { vars } from "../../theme/theme.css";
import { currencyFormatter } from "../../utils/functions";
import { FieldMessage } from "../FieldInfo/FieldMessage";
import { IconExclamation, IconEye, IconEyeSlash } from "../icons";
import { Spinner } from "../Spinner";
import {
  iconSizeVariants,
  inputClass,
  inputContainerClass,
  inputContainerSizeVariants,
  inputWrapperClass,
} from "./styles.css";

export type InputSize = "small" | "medium" | "large";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: React.ReactNode;
  info?: React.ReactNode;
  error?: string;
  size?: InputSize;
  isLoading?: boolean;
  mask?: string;
  containerStyles?: React.CSSProperties;
  isCurrency?: boolean;
  hasPrefix?: boolean;
  minimumFractionDigits?: number;
  onChange?: (e: any) => void;
  /** Callback fired when the value changes. Provided by BaseUI Input — works when wrapped in a BaseUI Field.Root. */
  onValueChange?: BaseInputProps["onValueChange"];
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  inputBackground?: string;
  inputClassName?: string;
}

const maskValue = (value: string, mask: string) => {
  if (!value || value.length === 0) return "";

  let maskedValue = "";
  let valueIndex = 0;

  const letterRegex = /^[a-zA-ZÀ-ÿ~^´`]+$/;

  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i];
    const inputChar = value[valueIndex];

    if (!inputChar) break;

    if (maskChar === "9") {
      if (/\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else {
        break;
      }
    } else if (maskChar === "a") {
      if (letterRegex.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else {
        break;
      }
    } else if (maskChar === "*") {
      maskedValue += inputChar;
      valueIndex++;
    } else {
      maskedValue += maskChar;
      if (inputChar === maskChar) {
        valueIndex++;
      }
    }
  }

  return maskedValue;
};

export const Input = forwardRef(
  (
    {
      label,
      info,
      error,
      size = "large",
      isLoading,
      mask,
      containerStyles,
      isCurrency,
      hasPrefix,
      minimumFractionDigits,
      iconLeft,
      iconRight,
      inputBackground,
      inputClassName,
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const spinnerSize = size === "large" ? 18 : 16;
    const [passwordShowing, setPasswordShowing] = useState(false);
    const innerInputRef = useRef<HTMLInputElement | null>(null);

    const togglePasswordVisibility = () => {
      setPasswordShowing((prev) => !prev);
    };

    const isPasswordField = props.type === "password";
    const inputType = isPasswordField && passwordShowing ? "text" : props.type;

    const passwordIcon = isPasswordField ? (
      <button
        type="button"
        aria-label="Toggle password visibility"
        onClick={togglePasswordVisibility}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {passwordShowing ? <IconEyeSlash /> : <IconEye />}
      </button>
    ) : (
      iconRight
    );

    const valueFormatted =
      props.value !== undefined
        ? mask
          ? maskValue(props.value.toString(), mask)
          : props.value
        : undefined;

    const assignInputRef = (el: HTMLInputElement | null) => {
      innerInputRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };

    const handleContainerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && innerInputRef.current) {
        e.preventDefault();
        innerInputRef.current.focus();
      }
    };

    return (
      <Field.Root
        className={`${inputWrapperClass} ${props.className || ""}`}
        style={{ width: props.style?.width, ...containerStyles }}
        invalid={!!error}
        data-slot="input-root"
      >
        {label && (
          <Field.Label data-size={size} data-slot="input-label">
            {label}
          </Field.Label>
        )}
        <div
          className={`${inputContainerClass} ${inputContainerSizeVariants[size]} ${inputClassName || ""}`}
          style={{ backgroundColor: inputBackground }}
          data-slot="input-container"
          data-invalid={error ? "true" : undefined}
          onMouseDown={handleContainerMouseDown}
        >
          {iconLeft && (
            <span className={`icon-left ${iconSizeVariants[size]}`}>
              {iconLeft}
            </span>
          )}
          {isCurrency ? (
            <CurrencyInput
              hasPrefix={hasPrefix}
              minimumFractionDigits={minimumFractionDigits}
              {...props}
              style={props.style}
              type={inputType}
              ref={assignInputRef}
            />
          ) : (
            <NormalInput
              isLoading={isLoading}
              {...props}
              value={valueFormatted}
              type={inputType}
              ref={assignInputRef}
              mask={mask}
              onChange={(e) => {
                if (mask) {
                  const maskedValue = maskValue(e.target.value, mask);
                  e.target.value = maskedValue;
                }
                props.onChange?.(e);
              }}
            />
          )}
          {isLoading ? (
            <Spinner size={spinnerSize} color={vars.colors.neutral.light} />
          ) : error ? (
            <IconExclamation
              className={`error-icon ${iconSizeVariants[size]}`}
            />
          ) : (
            passwordIcon && (
              <span className={`icon-right ${iconSizeVariants[size]}`}>
                {passwordIcon}
              </span>
            )
          )}
        </div>

        <FieldMessage
          error={error}
          info={info}
          isDisabled={props.disabled || isLoading}
        />
      </Field.Root>
    );
  },
);

Input.displayName = "Input";

interface NormalInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mask?: string;
  isLoading?: boolean;
  onValueChange?: BaseInputProps["onValueChange"];
}

const NormalInput = forwardRef(
  (
    { isLoading, mask, ...props }: NormalInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <BaseInput
        {...props}
        disabled={isLoading || props.disabled}
        className={inputClass}
        ref={ref}
      />
    );
  },
);

NormalInput.displayName = "NormalInput";

interface CurrencyInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  hasPrefix?: boolean;
  minimumFractionDigits?: number;
  isLoading?: boolean;
}

const CurrencyInput = forwardRef(
  (
    {
      hasPrefix,
      minimumFractionDigits = 2,
      value,
      isLoading,
      ...props
    }: CurrencyInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const multiplicator = 10 ** minimumFractionDigits;
    const inputElementRef = useRef<HTMLInputElement | null>(null);

    const assignInputRef = (el: HTMLInputElement | null) => {
      inputElementRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };

    const handleKeyDownCommon = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "0" && e.currentTarget.value.length === 0) {
        setTimeout(() => {
          const el = inputElementRef.current;
          if (el) {
            const end = el.value.length;
            try {
              el.setSelectionRange(end, end);
            } catch {}
          }
        }, 1);
      }
    };

    return (
      <NumberFormatBase
        format={(e) =>
          currencyFormatter(
            parseFloat(e),
            hasPrefix,
            minimumFractionDigits,
            Number(props.max),
            Number(props.min),
          )
        }
        {...props}
        value={Number(value) * multiplicator}
        className={inputClass}
        defaultValue={props.defaultValue as string | number | undefined | null}
        disabled={isLoading || props.disabled}
        onKeyDown={(e) => {
          handleKeyDownCommon(e);
          if (e.key === "Backspace") {
            const el = inputElementRef.current;
            const digits = (el?.value || "").replace(/\D/g, "");
            const numeric = digits.length ? parseInt(digits, 10) : 0;

            if (numeric === 0) {
              e.preventDefault();
              const event = new Event("change", { bubbles: true }) as any;
              Object.defineProperty(event, "target", {
                value: {
                  value: "",
                  name: props.name ?? "",
                  type: "text",
                },
                enumerable: true,
              });
              Object.defineProperty(event, "currentTarget", {
                value: {
                  value: "",
                  name: props.name ?? "",
                  type: "text",
                },
                enumerable: true,
              });
              props.onChange?.(event as React.ChangeEvent<HTMLInputElement>);
              if (el) {
                el.value = "";
                try {
                  el.setSelectionRange(0, 0);
                } catch {}
              }
            }
          }
        }}
        onChange={undefined}
        onValueChange={(e) => {
          if (e.floatValue === 0) {
            setTimeout(() => {
              const el = inputElementRef.current;
              if (el) {
                el.setSelectionRange(el.value.length, el.value.length);
              }
            }, 1);
          }
          const numericValue =
            e.floatValue || e.floatValue === 0
              ? e.floatValue / multiplicator
              : undefined;
          const valueStr =
            numericValue !== undefined ? numericValue.toString() : "";
          const event = new Event("change", { bubbles: true }) as any;
          Object.defineProperty(event, "target", {
            value: {
              value: valueStr,
              name: props.name ?? "",
              type: "text",
            },
            enumerable: true,
          });
          Object.defineProperty(event, "currentTarget", {
            value: {
              value: valueStr,
              name: props.name ?? "",
              type: "text",
            },
            enumerable: true,
          });
          props.onChange?.(event as React.ChangeEvent<HTMLInputElement>);
        }}
        type="text"
        getInputRef={assignInputRef}
      />
    );
  },
);

CurrencyInput.displayName = "CurrencyInput";
